/**
 * 서류(이미지 자산) 패널 — 기사·차량·거래처·소속 상세에 붙는다.
 *
 * 레거시는 등록 화면 안에 이미지 칸이 박혀 있었지만, 여기서는 **목록 → 눌러서 열기**다.
 * 이유: 자동차등록증 한 장이 평균 2.5MB(실측)라 상세를 열 때마다 다 받으면 화면이 멈춘다.
 *
 * 파일은 `Authorization` 헤더가 필요해 `<img src>` 로 바로 못 건다 — 눌렀을 때 받아서 blob URL 로 띄운다.
 * 열었던 URL 은 닫을 때 해제한다(안 하면 탭이 메모리를 계속 문다).
 *
 * ⚠️ **레거시 버그는 재현하지 않는다**(SPEC §6-3). 레거시 `usp_Car_Update`/`usp_Driver_Update` 는
 *    이미지 파라미터가 안 오면 기존 이미지를 지웠다 — 저장 한 번에 서류 7장이 날아갔다.
 *    여기서는 서류가 **기준정보 저장과 완전히 분리된 동작**이다. 폼을 저장해도 서류는 건드리지 않는다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api, fetchFileAssetUrl, uploadFileAsset,
  type FileAssetItem, type FileOwnerType,
} from '../lib/api';
import { downloadImageAs, type ExportFormat } from '../lib/imageExport';

const KB = 1024;
const fmtSize = (n: number) => (n >= KB * KB ? `${(n / KB / KB).toFixed(1)} MB` : `${Math.round(n / KB)} KB`);

/** 종류 목록은 서버 라벨 표와 같아야 한다(`functions/src/fileAsset.ts` KIND_LABEL). */
const KINDS: Record<FileOwnerType, { kind: string; label: string }[]> = {
  driver: [
    { kind: 'ImgDriver', label: '기사 사진' },
    { kind: 'ImgDriveLic', label: '운전면허증' },
    { kind: 'ImgBusLic', label: '버스운전자격증' },
    { kind: 'ImaCareer', label: '운전경력확인서' },
    { kind: 'ImgDriveTest', label: '운전정밀검사증' },
    { kind: 'ImgReside', label: '주민등록등본' },
    { kind: 'ImgResume', label: '이력서' },
    { kind: 'ImgBosuEdu', label: '보수교육필증' },
    { kind: 'ImgBank', label: '통장사본' },
  ],
  car: [
    { kind: 'ImgReg', label: '자동차등록증' },
    { kind: 'ImgIns', label: '보험등록증' },
    { kind: 'ImgCar1', label: '차량사진' },
  ],
  cust: [
    { kind: 'ImgBiz', label: '사업자등록증' },
    { kind: 'ImgContract', label: '계약서' },
  ],
  grp: [
    { kind: 'ImgBiz', label: '사업자등록증' },
    { kind: 'ImgBizTrans', label: '운송사업등록증' },
    { kind: 'ImgBank', label: '통장사본' },
  ],
};

export default function FileAssets({
  ownerType, ownerId, canEdit = false, pending, onPending,
}: {
  ownerType: FileOwnerType;
  /** null = 아직 등록 전(#140). 고른 파일은 `pending` 에만 담아 두고, 폼이 「등록」 뒤에 올린다. */
  ownerId: number | null;
  canEdit?: boolean;
  pending?: Record<string, File>;
  onPending?: (kind: string, file: File | null) => void;
}) {
  const before = ownerId === null;
  const [items, setItems] = useState<FileAssetItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<{ item: FileAssetItem; url: string } | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [busyKind, setBusyKind] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  /** 어떤 종류로 올리는지 — 파일 선택창을 열기 직전에 정한다. */
  const pendingKind = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    if (ownerId === null) { setItems([]); return; }
    setItems(null);
    setError(null);
    api.listFileAssets({ ownerType, ownerId })
      .then((r) => { if (alive) setItems(r.items); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [ownerType, ownerId, nonce]);

  const close = useCallback(() => {
    setOpen((cur) => { if (cur) URL.revokeObjectURL(cur.url); return null; });
  }, []);

  // 화면을 벗어날 때도 해제한다.
  useEffect(() => () => { if (open) URL.revokeObjectURL(open.url); }, [open]);

  const [exporting, setExporting] = useState(false);
  async function exportAs(fmt: ExportFormat) {
    if (!open) return;
    setExporting(true);
    setError(null);
    try {
      await downloadImageAs(open.url, open.item.contentType, open.item.label, fmt);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  async function show(item: FileAssetItem) {
    setLoadingId(item.fileAssetId);
    setError(null);
    try {
      const url = await fetchFileAssetUrl(item.fileAssetId);
      setOpen((cur) => { if (cur) URL.revokeObjectURL(cur.url); return { item, url }; });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingId(null);
    }
  }

  function pickFor(kind: string) {
    pendingKind.current = kind;
    setError(null);
    setNotice(null);
    fileInput.current?.click();
  }

  async function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const kind = pendingKind.current;
    e.target.value = ''; // 같은 파일을 다시 고를 수 있게
    if (!file || !kind) return;
    if (ownerId === null) { onPending?.(kind, file); pendingKind.current = null; return; }

    setBusyKind(kind);
    setError(null);
    setNotice(null);
    try {
      const r = await uploadFileAsset(ownerType, ownerId, kind, file);
      setNotice(`${r.label} ${r.replaced ? '교체' : '등록'} 완료 (${fmtSize(r.byteSize)})`);
      close();
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
      pendingKind.current = null;
    }
  }

  async function remove(item: FileAssetItem) {
    // 서류는 되돌리기 어려운 편이라 한 번 묻는다. 실제로는 soft delete 라 복구 가능하지만,
    // 그 사실을 화면에서 약속하지는 않는다(복구는 담당자가 직접 못 한다).
    if (!confirm(`${item.label} 을(를) 삭제할까요?`)) return;
    setBusyKind(item.kind);
    setError(null);
    setNotice(null);
    try {
      await api.deleteFileAsset({ fileAssetId: item.fileAssetId });
      setNotice(`${item.label} 삭제됨`);
      close();
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  const byKind = new Map((items ?? []).map((i) => [i.kind, i]));
  // 등록된 것 먼저, 그다음 빈 칸. 담당자가 "무엇이 없는지"를 바로 본다 —
  // 목록만 주면 없는 서류는 화면에 아예 안 나와서 누락을 눈치채지 못한다.
  const slots = KINDS[ownerType];

  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-bold">서류</h3>
        {items && !before && (
          <span className="text-xs text-ink-faint">
            {items.length}/{slots.length}
          </span>
        )}
        {before && <span className="text-xs text-ink-faint">고른 파일은 「등록」을 누르면 함께 올라갑니다</span>}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      {notice && <p className="text-xs text-emerald-700">{notice}</p>}
      {items === null && !error && <p className="text-xs text-ink-faint">불러오는 중…</p>}

      {items && (
        <ul className="flex flex-wrap gap-2">
          {slots.map(({ kind, label }) => {
            const it = byKind.get(kind);
            const busy = busyKind === kind;
            const wait = pending?.[kind];
            if (before) {
              return (
                <li key={kind} className="flex items-center gap-1 rounded-lg border border-line px-1.5 py-1">
                  <span className={`px-1.5 text-xs ${wait ? 'text-emerald-700' : 'text-ink-faint'}`}>
                    {label} {wait ? <span title={wait.name}>✓ {fmtSize(wait.size)}</span> : <span className="opacity-60">없음</span>}
                  </span>
                  {canEdit && (
                    <button type="button" className="btn-ghost px-1.5 text-xs" onClick={() => pickFor(kind)}>{wait ? '바꾸기' : '선택'}</button>
                  )}
                  {canEdit && wait && (
                    <button type="button" className="btn-ghost px-1.5 text-xs text-red-700" onClick={() => onPending?.(kind, null)}>빼기</button>
                  )}
                </li>
              );
            }
            return (
              <li key={kind} className="flex items-center gap-1 rounded-lg border border-line px-1.5 py-1">
                {it ? (
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => show(it)}
                    disabled={loadingId === it.fileAssetId || busy}
                  >
                    {label}
                    <span className="ml-1.5 text-ink-faint">
                      {loadingId === it.fileAssetId ? '여는 중…' : fmtSize(it.byteSize)}
                    </span>
                  </button>
                ) : (
                  <span className="px-1.5 text-xs text-ink-faint">{label} <span className="opacity-60">없음</span></span>
                )}

                {canEdit && (
                  <>
                    <button
                      type="button"
                      className="btn-ghost px-1.5 text-xs"
                      onClick={() => pickFor(kind)}
                      disabled={busy}
                      title={it ? '교체' : '등록'}
                    >
                      {busy ? '올리는 중…' : it ? '교체' : '등록'}
                    </button>
                    {it && (
                      <button
                        type="button"
                        className="btn-ghost px-1.5 text-xs text-red-700"
                        onClick={() => remove(it)}
                        disabled={busy}
                      >
                        삭제
                      </button>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={onPicked}
          />
          <p className="text-xs text-ink-faint">
            PNG·JPEG 만 등록됩니다 — <b>파일 내용으로 판정</b>하므로 확장자만 바꾼 파일은 거부됩니다.
            교체해도 <b>이전 파일은 지워지지 않습니다</b>(목록에서만 내려갑니다).
          </p>
        </>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/80 p-4"
          role="dialog"
          aria-label={open.item.label}
          onClick={close}
        >
          <div className="mb-2 flex items-center gap-3 text-white" onClick={(e) => e.stopPropagation()}>
            <span className="font-bold">{open.item.label}</span>
            <span className="text-xs opacity-70">{fmtSize(open.item.byteSize)}</span>
            {error && <span className="text-xs text-red-300">{error}</span>}
            <span className="ml-auto text-xs opacity-70">내려받기</span>
            {(['png', 'jpg', 'pdf'] as const).map((f) => (
              <button key={f} type="button" className="btn-ghost bg-white/10 text-xs text-white" disabled={exporting}
                      onClick={() => void exportAs(f)}>
                {f.toUpperCase()}
              </button>
            ))}
            <button type="button" className="btn-ghost bg-white/10 text-xs text-white" onClick={close}>
              닫기
            </button>
          </div>
          <img
            src={open.url}
            alt={open.item.label}
            className="mx-auto max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
